import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ArticleCard from "../components/ArticleCard";
import { api, timeAgo } from "../lib/api";
import { CATEGORIES, getCategory } from "../lib/categories";
import { Radio, TrendingUp, ArrowRight } from "lucide-react";

const SectionTitle = ({ children, color = "#0F172A" }) => (
  <div className="flex items-center gap-3 mb-6">
    <span className="w-1.5 h-7 rounded" style={{ background: color }} />
    <h2 className="font-headline text-2xl font-black text-[#0A0A0A]">{children}</h2>
  </div>
);

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [latest, setLatest] = useState([]);
  const [popular, setPopular] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Le Véridique — Actualités en continu";
    Promise.all([
      api.get("/articles", { params: { featured: true, limit: 4 } }),
      api.get("/articles", { params: { limit: 13 } }),
      api.get("/articles", { params: { popular: true, limit: 5 } }),
    ]).then(([f, l, p]) => {
      setFeatured(f.data.articles || []);
      setLatest(l.data.articles || []);
      setPopular(p.data.articles || []);
    }).finally(() => setLoading(false));
  }, []);

  const hero = featured[0] || latest[0];
  const heroSide = (featured.length ? featured.slice(1, 4) : latest.slice(1, 4));
  const usedIds = new Set([hero, ...heroSide].filter(Boolean).map((a) => a.id));
  const feed = latest.filter((a) => !usedIds.has(a.id)).slice(0, 9);

  const isEmpty = !loading && latest.length === 0;

  return (
    <div className="App">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading && <div className="py-20 text-center meta-label text-slate-400">Chargement…</div>}

        {isEmpty && (
          <div className="py-24 text-center">
            <h2 className="font-headline text-2xl font-black text-[#0A0A0A] mb-3">Bienvenue sur Le Véridique</h2>
            <p className="font-body text-slate-500 mb-6">Aucun article publié pour le moment. Connectez-vous à l'espace rédaction pour publier votre première actualité.</p>
            <Link to="/admin" className="inline-flex items-center gap-2 bg-[#0F172A] text-white px-6 py-3 rounded-full font-sub font-semibold" data-testid="empty-admin-cta">
              Espace rédaction <ArrowRight size={16} />
            </Link>
          </div>
        )}

        {!loading && hero && (
          <>
            {/* À la une */}
            <div className="flex items-center gap-2 mb-4">
              <span className="bg-[#E11D48] text-white meta-label font-bold px-2.5 py-1 rounded flex items-center gap-1.5"><Radio size={12} className="live-dot" /> À la une</span>
            </div>
            <section className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-12">
              <div className="lg:col-span-8"><ArticleCard article={hero} variant="hero" /></div>
              <div className="lg:col-span-4 flex flex-col gap-4">
                {heroSide.map((a) => <ArticleCard key={a.id} article={a} variant="side" />)}
              </div>
            </section>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              {/* Latest feed */}
              <div className="lg:col-span-8">
                <SectionTitle>Derniers articles</SectionTitle>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {feed.map((a) => <ArticleCard key={a.id} article={a} />)}
                </div>
              </div>

              {/* Sidebar */}
              <aside className="lg:col-span-4 space-y-10">
                {/* Dernières nouvelles */}
                <div>
                  <div className="flex items-center gap-2 mb-5">
                    <Radio size={16} className="text-[#E11D48] live-dot" />
                    <h2 className="font-headline text-xl font-black text-[#0A0A0A]">Dernières nouvelles</h2>
                  </div>
                  <div className="space-y-4 relative pl-4 border-l-2 border-[#E2E8F0]">
                    {latest.slice(0, 6).map((a) => (
                      <Link key={a.id} to={`/${a.category}/${a.slug}`} className="block group relative" data-testid={`live-item-${a.slug}`}>
                        <span className="absolute -left-[21px] top-1.5 w-2.5 h-2.5 rounded-full bg-[#E11D48]" />
                        <span className="meta-label text-slate-400">{timeAgo(a.published_at)}</span>
                        <p className="font-sub text-sm font-medium text-[#1E293B] group-hover:text-[#E11D48] leading-snug mt-0.5">{a.title}</p>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Popular */}
                {popular.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-5">
                      <TrendingUp size={16} className="text-[#E11D48]" />
                      <h2 className="font-headline text-xl font-black text-[#0A0A0A]">Articles les plus lus</h2>
                    </div>
                    <div className="space-y-4">
                      {popular.map((a, i) => (
                        <Link key={a.id} to={`/${a.category}/${a.slug}`} className="flex gap-4 group items-start" data-testid={`popular-item-${a.slug}`}>
                          <span className="font-headline text-3xl font-black text-[#E2E8F0] leading-none w-10 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                          <div>
                            <p className="font-headline text-base font-bold leading-snug text-[#0A0A0A] group-hover:text-[#E11D48]">{a.title}</p>
                            <span className="meta-label text-slate-400">{a.views} vues</span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </aside>
            </div>

            {/* Category blocks */}
            <div className="mt-16 space-y-14">
              {CATEGORIES.filter((c) => !c.featuredPage).slice(0, 4).map((c) => (
                <CategoryStrip key={c.slug} category={c} />
              ))}
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

function CategoryStrip({ category }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/articles", { params: { category: category.slug, limit: 3 } })
      .then((r) => setItems(r.data.articles || [])).catch(() => {});
  }, [category.slug]);

  if (!items.length) return null;
  return (
    <section>
      <div className="flex items-center justify-between mb-6 border-b-2 border-[#0A0A0A] pb-2">
        <div className="flex items-center gap-3">
          <span className="w-1.5 h-7 rounded" style={{ background: category.color }} />
          <h2 className="font-headline text-2xl font-black text-[#0A0A0A]">{category.name}</h2>
        </div>
        <Link to={`/rubrique/${category.slug}`} className="meta-label text-[#E11D48] flex items-center gap-1 hover:gap-2 transition-all" data-testid={`strip-more-${category.slug}`}>
          Tout voir <ArrowRight size={13} />
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {items.map((a) => <ArticleCard key={a.id} article={a} />)}
      </div>
    </section>
  );
}
